import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { PublicationNewAliasRequest } from '@/module/Author/Publication/model/PublicationTypes';
import { TextFilters } from '@/module/Author/Text/model/TextTypes';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './BookApi';
import { Book, BookRequest, BookSearchRequest } from './BookTypes';

const bookKey = getQueryKey('book');

export function getBookPagedOptions(params: PagedParams<TextFilters>): UseSuspenseQueryOptions<PagedResponse<Book>> {
  return {
    queryKey: bookKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getBookOptions(bookId: Id): UseSuspenseQueryOptions<Book> {
  return {
    queryKey: bookKey.one(bookId),
    queryFn: () => api.get(bookId),
  };
}

export function useBookEdit(bookId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: BookRequest) => api.edit(bookId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: bookKey.root(),
      }),
  });
}

export function useBookCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: BookRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: bookKey.root(),
      }),
  });
}

export function useBookDelete(bookId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(bookId),
    onSuccess: () => invalidateAfterDelete(queryClient, bookKey, bookId),
  });
}

export function useSearchBookByIdentifiers() {
  return useMutation({
    mutationFn: (request: BookSearchRequest) => api.searchByIdentifiers(request),
  });
}

export function useBookAddNewAlias() {
  const queryClient = useQueryClient();
  return useMutation<Book, Error, { bookId: Id; request: PublicationNewAliasRequest }>({
    mutationFn: ({ bookId, request }) => api.addNewAlias(bookId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: bookKey.root(),
      }),
  });
}
